import { spawn } from "node:child_process";
import { readFile, writeFile } from "node:fs/promises";
import { existsSync } from "node:fs";
import path from "node:path";
import { Command } from "commander";
import {
  findProjectRoot,
  resolveHaivePaths,
  serializeMemory,
  type MemoryType,
  parseMemory,
} from "@hivelore/core";
import { loadMemoriesFromDir } from "../utils/fs.js";
import { ui } from "../utils/ui.js";

interface UpdateOptions {
  edit?: boolean;
  editor?: string;
  type?: MemoryType;
  title?: string;
  body?: string;
  bodyFile?: string;
  tags?: string;
  paths?: string;
  files?: string;
  symbols?: string;
  commit?: string;
  domain?: string;
  author?: string;
  dir?: string;
}

export function registerMemoryUpdate(memory: Command): void {
  memory
    .command("update <id>")
    .description("Update body, tags, or anchor of an existing memory (preserves id and usage history)")
    .option("--type <type>", "change the memory type (convention | decision | gotcha | architecture | glossary | skill | attempt)")
    .option("--title <text>", "new title — replaces the first heading of the body")
    .option("--body <text>", "new Markdown body — replaces the existing body")
    .option("--body-file <path>", "read new body from a Markdown file — for long content")
    .option("--tags <csv>", "new tags, comma-separated — fully replaces existing tags")
    .option("--paths <csv>", "new anchor paths, comma-separated")
    .option("--files <csv>", "alias for --paths (matches the MCP `files` parameter)")
    .option("--symbols <csv>", "new anchor symbols, comma-separated")
    .option("--commit <sha>", "new anchor commit SHA")
    .option("--domain <domain>", "new domain label")
    .option("--author <author>", "new author handle or email")
    .option("--edit", "open the memory file in $EDITOR instead of flag-based edits (absorbed `memory edit`)")
    .option("-e, --editor <cmd>", "with --edit: editor command (defaults to $EDITOR or 'vi')")
    .option("-d, --dir <dir>", "project root")
    .action(async (id: string, opts: UpdateOptions) => {
      const root = findProjectRoot(opts.dir);
      const paths = resolveHaivePaths(root);
      if (opts.edit) {
        // Absorbed `memory edit` (v0.32.0): interactive path, re-validated on save.
        const all = existsSync(paths.memoriesDir) ? await loadMemoriesFromDir(paths.memoriesDir) : [];
        const found = all.find((m) => m.memory.frontmatter.id === id);
        if (!found) {
          ui.error(`No memory with id "${id}".`);
          process.exitCode = 1;
          return;
        }
        const editor = opts.editor ?? process.env.EDITOR ?? process.env.VISUAL ?? "vi";
        ui.info(`Opening ${path.relative(root, found.filePath)} with ${editor}…`);
        const code = await new Promise<number>((resolve) => {
          const child = spawn(editor, [found.filePath], { stdio: "inherit" });
          child.on("exit", (c) => resolve(c ?? 0));
          child.on("error", () => resolve(127));
        });
        if (code !== 0) ui.warn(`Editor exited with status ${code}.`);
        try {
          parseMemory(await readFile(found.filePath, "utf8"));
          ui.success("Memory still parses cleanly.");
        } catch (err) {
          ui.error(`Memory no longer parses: ${err instanceof Error ? err.message : String(err)}`);
          ui.warn("File left as-is on disk; fix it and re-run a parse-aware command to confirm.");
          process.exitCode = 1;
        }
        return;
      }
      if (!existsSync(paths.memoriesDir)) {
        ui.error(`No .ai/memories at ${root}. Run \`hivelore init\` first.`);
        process.exitCode = 1;
        return;
      }

      const memories = await loadMemoriesFromDir(paths.memoriesDir);
      const loaded = memories.find((m) => m.memory.frontmatter.id === id);
      if (!loaded) {
        ui.error(`No memory with id "${id}".`);
        process.exitCode = 1;
        return;
      }

      const updated: string[] = [];
      const { frontmatter, body } = loaded.memory;

      const newAnchor = { ...frontmatter.anchor };
      const pathsOpt = opts.paths ?? opts.files;
      if (pathsOpt !== undefined) {
        newAnchor.paths = parseCsv(pathsOpt);
        updated.push("anchor.paths");
      }
      if (opts.symbols !== undefined) {
        newAnchor.symbols = parseCsv(opts.symbols);
        updated.push("anchor.symbols");
      }
      if (opts.commit !== undefined) {
        newAnchor.commit = opts.commit;
        updated.push("anchor.commit");
      }

      const newFrontmatter = {
        ...frontmatter,
        anchor: newAnchor,
        ...(opts.type !== undefined ? { type: opts.type } : {}),
        ...(opts.tags !== undefined ? { tags: parseCsv(opts.tags) } : {}),
        ...(opts.domain !== undefined ? { domain: opts.domain } : {}),
        ...(opts.author !== undefined ? { author: opts.author } : {}),
      };
      if (opts.type !== undefined) updated.push("type");
      if (opts.tags !== undefined) updated.push("tags");
      if (opts.domain !== undefined) updated.push("domain");
      if (opts.author !== undefined) updated.push("author");

      // Body resolution: --body-file > --body > existing body
      let newBody: string;
      if (opts.bodyFile !== undefined) {
        if (!existsSync(opts.bodyFile)) {
          ui.error(`--body-file not found: ${opts.bodyFile}`);
          process.exitCode = 1;
          return;
        }
        newBody = await readFile(opts.bodyFile, "utf8");
        updated.push("body");
      } else if (opts.body !== undefined) {
        newBody = opts.body;
        updated.push("body");
      } else {
        newBody = body;
      }

      if (opts.title !== undefined) {
        newBody = replaceFirstHeading(newBody, opts.title);
        updated.push("title");
      }

      if (updated.length === 0) {
        ui.warn("Nothing to update — provide at least one option.");
        return;
      }

      await writeFile(
        loaded.filePath,
        serializeMemory({ frontmatter: newFrontmatter, body: newBody }),
        "utf8",
      );

      ui.success(`Updated ${path.relative(root, loaded.filePath)}`);
      ui.info(`fields: ${updated.join(", ")}`);
    });
}

function replaceFirstHeading(body: string, title: string): string {
  const headingRe = /^#\s+.+$/m;
  const replacement = `# ${title}`;
  if (headingRe.test(body)) {
    return body.replace(headingRe, replacement);
  }
  return `${replacement}\n\n${body}`;
}

function parseCsv(value: string): string[] {
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}
